import { Mood, SecondaryMood } from '@peekapak/types/Mood';
import { GradeBand } from '@peekapak/types/EmotionsClassification';
import { values as checkInValues } from './CheckIn/checkInValues';
import {
  getElementaryMoodColorFill,
  getElementaryMoodTextColor,
  getSecondaryMoodTextColor,
} from './AdministratorDashboard/MoodboardAdmin/moodboardUtilities';

export interface EmotionColor {
  backgroundColor: string;
  textColor: string;
}

// DateRangePicker returns the selected end date at local midnight - without this,
// check-ins later that same day would be excluded from the range.
export function getEndOfDay(date: Date): number {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.getTime();
}

// checkInValues.ts is the actual 6-12 check-in UI - source of truth for these
// fill colors per PRD (tags must match what students see at check-in).
const SIX_TO_TWELVE_FILL: Record<string, string> = Object.fromEntries(
  checkInValues.map((category) => [category.title, category.segmentColor]),
);

// A 6-12 check-in's feeling is normally a sub-feeling of the seven-category wheel. Check sub-feelings first,
// since a sub-feeling can share its string with an unrelated Mood (e.g. SecondaryMood.SCARED.NERVOUS ===
// Mood.NERVOUS) - matching the sub-feeling first resolves that collision correctly. Returns undefined, rather
// than throwing, when nothing matches - a Mood value (PK-5's six-mood set) is handled separately by the caller.
function resolveSixToTwelveCategory(feeling: Mood | SecondaryMood): string | undefined {
  const categoryKey = Object.keys(SecondaryMood).find((key) =>
    (Object.values(SecondaryMood[key as keyof typeof SecondaryMood]) as string[]).includes(feeling),
  );
  return categoryKey ? Mood[categoryKey as keyof typeof Mood] : undefined;
}

// Overrides the shared Moodboard fill for Sad/Angry to match this report's own mockup;
// left unset (falls through to the shared colors) for anything else.
const PK5_FILL_OVERRIDE: Partial<Record<string, string>> = {
  [Mood.SAD]: '#76BEE0',
  [Mood.ANGRY]: '#619ECC',
};

function pk5Color(feeling: Mood | SecondaryMood): EmotionColor {
  return {
    backgroundColor: PK5_FILL_OVERRIDE[feeling] ?? getElementaryMoodColorFill(feeling),
    textColor: getElementaryMoodTextColor(feeling),
  };
}

export function getEmotionColor(feeling: Mood | SecondaryMood, gradeBand: GradeBand): EmotionColor {
  if (gradeBand === 'PK-5') {
    return pk5Color(feeling);
  }

  const category = resolveSixToTwelveCategory(feeling);
  if (category) {
    return {
      backgroundColor: SIX_TO_TWELVE_FILL[category],
      textColor: getSecondaryMoodTextColor(category),
    };
  }

  // Not a recognized sub-feeling. A Mood value belongs to PK-5's six-mood set regardless of gradeBand - the
  // seven-category wheel's colors are keyed by its own category names, not PK-5's mood names, so looking up
  // a Mood value there can miss (e.g. "Excited" and "Nervous" aren't wheel category names).
  if ((Object.values(Mood) as string[]).includes(feeling)) {
    return pk5Color(feeling);
  }

  throw new Error(`Invalid feeling: ${feeling}`);
}
